import { stepperAnatomy as parts } from "@chakra-ui/anatomy";
import type { PartsStyleObject } from "@chakra-ui/theme-tools";

// Circle diameters, and the offset that keeps the connector level with their centres. The
// diameter is exported because a step's column must be sized to it - see the title style.
export const stepperIndicatorSize = ["icon.lg", "icon.lg", "icon.xl"];
const separatorOffset = ["16px", "16px", "20px"];
// Space between a circle and the connector on either side of it. Figma's own gaps run 7-10px,
// varying only because its step columns are label-width rather than a fixed diameter.
const stepperConnectorGap = "1.6";

// Chakra's own base style merges underneath this one, so the horizontal overrides have to sit
// in the same `[data-orientation]` selectors it uses or they lose on specificity.
const baseStyle: PartsStyleObject<typeof parts> = {
    stepper: {
        w: "100%",
        // Chakra puts each connector inside its own step, so the space on a circle's left comes
        // from this gap and the space on its right from the step's. Equal values centre it.
        gap: stepperConnectorGap,
        "&[data-orientation=horizontal]": {
            alignItems: "flex-start",
        },
    },
    step: {
        "&[data-orientation=horizontal]": {
            alignItems: "flex-start",
            gap: stepperConnectorGap,
        },
    },
    indicator: {
        // Every radii token in this theme is 0, so a circle cannot come from borderRadius="full".
        borderRadius: "50%",
        width: stepperIndicatorSize,
        height: stepperIndicatorSize,
        bg: "gray.50",
        color: "gray.500",
        "&[data-status=active]": {
            bg: "white",
            color: "black",
            borderWidth: "2px",
            borderColor: "black",
        },
        "&[data-status=complete]": {
            bg: "black",
            color: "white",
            borderWidth: 0,
        },
        "&[data-status=incomplete]": {
            borderWidth: 0,
        },
    },
    number: {
        fontSize: ["mobileBodyTextTiny", "mobileBodyTextTiny", "desktopBodyTextSmall"],
        fontWeight: "bold",
        lineHeight: "shorter",
    },
    title: {
        // A title wider than the indicator would shorten its own step's connector, as Chakra
        // nests each connector inside its step. Measuring as nothing keeps them all equal.
        display: ["none", "none", "flex"],
        // Centres the overflow on the indicator; text-align cannot, as Chrome will not
        // centre text that overflows its line box.
        justifyContent: "center",
        width: "0px",
        minWidth: "0px",
        mt: "1.2",
        fontSize: "desktopBodyTextSmall",
        fontWeight: "bold",
        color: "gray.500",
        whiteSpace: "nowrap",
        "&[data-status=active], &[data-status=complete]": {
            color: "black",
        },
    },
    separator: {
        bg: "transparent",
        borderTopWidth: "1px",
        borderTopStyle: "dashed",
        borderColor: "gray.200",
        "&[data-status=complete]": {
            bg: "transparent",
        },
        "&[data-orientation=horizontal]": {
            h: 0,
            ms: 0,
            mt: separatorOffset,
        },
    },
};

export const stepperStyles = {
    parts: parts.keys,
    baseStyle,
};
